import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { TenantConfigService } from '@core/services/tenant-config.service';
import { Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, take } from 'rxjs/operators';
import { ITenant } from '@core/models/tenant.model';
import { SettlementAttachmentSocket } from '@features/settlements/models/settlement';
import { SocketService } from '@core/services/socket.service';
import { Permission } from '@core/enums/permission.enum';
import { intersection } from 'lodash-es';
import { Store } from '@ngrx/store';
import { initOptionsSelectors } from '@store/app/init-options/init-options.selectors';

@Injectable({
  providedIn: 'root',
})
export class SettlementsSocketService extends SocketService {
  private subscriptions: string[] = [];
  private messagesMap: Map<string, Subject<SettlementAttachmentSocket>> = new Map();

  private readonly permissions = [Permission.PERM_DOCUMENTS_INVOICES];
  protected readonly url: string = '/hub/document';
  protected readonly listenersToClose: string[] = ['AttachmentGenerated'];

  private isUiVersion$ = this._store.select(initOptionsSelectors.uiVersion).pipe(
    map((version) => ['arval'].includes((version || '').toLowerCase())),
    filter(Boolean),
    take(1)
  );
  private readonly tenantCode$: Observable<string | null> = this._tenantConfigService
    .getTenant(this.keycloakService.getKeycloakInstance().tokenParsed.given_name)
    .pipe(
      map((tenant: ITenant) => tenant?.code ?? null),
      shareReplay(1)
    );

  constructor(
    protected keycloakService: KeycloakService,
    private _tenantConfigService: TenantConfigService,
    private _store: Store
  ) {
    super(keycloakService);
    const hasAccess = intersection(this.keycloakService.getUserRoles(), this.permissions).length === this.permissions.length;
    if (hasAccess) {
      this.isUiVersion$.subscribe(() => {
        this.startConnection(() => {
          this.hubConnection.on('AttachmentGenerated', (data: SettlementAttachmentSocket) => {
            this.messagesMap.get(data.documentGuid)?.next(data);
          });
        });
      });
    }
  }

  getMessageByGuid(guid: string): Observable<SettlementAttachmentSocket> {
    if (!this.messagesMap.has(guid)) {
      this.messagesMap.set(guid, new Subject());
    }
    return this.messagesMap.get(guid).asObservable();
  }

  isSubscribed(guid: string): boolean {
    return this.subscriptions.includes(guid);
  }

  startConnection(callback: () => void): void {
    if (this.hubConnection) {
      callback();
      return;
    }
    super.connect();

    this.hubConnection.onreconnected(() => {
      callback();
      for (const subscription of this.subscriptions) {
        this.subscribeDocument(subscription, () => {}, true);
      }
    });

    this.hubConnection
      .start()
      .then(() => callback())
      .catch((err) => console.error('Błąd połączenia:', err));
  }

  subscribeDocument(documentGuid: string, callback: () => void, reconnected = false): void {
    if (this.subscriptions.includes(documentGuid) && !reconnected) {
      callback();
      return;
    }

    this.tenantCode$.pipe(take(1), filter(Boolean)).subscribe((tenantCode: string) => {
      this.hubConnection
        .invoke('SubscribeDocument', tenantCode, documentGuid)
        .then(() => this.subscriptions.push(documentGuid))
        .then(() => callback())
        .catch((err) => console.error(`Błąd subskrypcji dokumentu ${documentGuid} dla ${tenantCode}: `, err));
    });
  }

  unsubscribeDocument(documentGuid: string): void {
    if (!this.subscriptions.includes(documentGuid)) {
      return;
    }
    this.tenantCode$.pipe(take(1), filter(Boolean)).subscribe((tenantCode: string) => {
      this.hubConnection
        .invoke('UnsubscribeDocument', tenantCode, documentGuid)
        .then(() => this.subscriptions = this.subscriptions.filter(id => id !== documentGuid))
        .catch((err) => {
          console.error(`Błąd odsubskrybowania dokumentu ${documentGuid}: `, err);
        });
    });
  }
}
